import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import {
  ORConnectionChangeOperationsForm,
  ORTechnicalDataForm
} from '@bo-client/shared/models/orderRequestWizard.model';
import {
  CivilEngineeringDisplayNames,
  CivilEngineeringEnum,
  ConnectionChangeRequestTypeDisplayValues,
  ConnectionChangeRequestTypeEnum
} from '@bo-client/shared/models/connectionChange.dto';
import { Observable, switchMap } from 'rxjs';
import { Observe } from '@bo-client/shared/observe.util';
import { combineLatestWith, distinctUntilChanged, map, shareReplay, startWith } from 'rxjs/operators';
import {
  AllAreaEnum,
  AreaDisplayName
} from '@bo-client/activation-management/shared/models/commissioningModel';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConnectionChangeRequestTypeConfig } from '@bo-client/shared/config.model';
import { FieldConfigEnum } from '@bo-client/shared/constants/enums';

@Component({
  selector: 'app-order-request-wizard-technical-data',
  templateUrl: './order-request-wizard-technical-data.component.html',
  styleUrls: ['./order-request-wizard-technical-data.component.scss']
})
export class OrderRequestWizardTechnicalDataComponent implements OnInit {
  @Input('formGroup')
  technicalDataForm: ORTechnicalDataForm;
  @Observe('technicalDataForm') technicalDataForm$: Observable<ORTechnicalDataForm>;
  @Input()
  availableAreas: { areas: AllAreaEnum[]; touched: boolean } = { areas: [], touched: false };
  @Observe('availableAreas') availableAreas$: Observable<{ areas: AllAreaEnum[]; touched: boolean }>;
  @Input() operations: ORConnectionChangeOperationsForm['value'];
  @Observe('operations') operations$: Observable<ORConnectionChangeOperationsForm['value']>;
  @Input() connectionChangeConfig: ConnectionChangeRequestTypeConfig;
  @Observe('connectionChangeConfig') connectionChangeConfig$: Observable<ConnectionChangeRequestTypeConfig>;

  private destroyRef = inject(DestroyRef);

  isCivilEngineering$: Observable<boolean>;
  hasNoMeter$: Observable<boolean>;
  powerUnit$: Observable<string>;
  multiAreaWithMeterActive$: Observable<boolean>;

  ngOnInit() {
    this.initObservables();
    this.initValidationListener();
    this.availableAreas$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(
          (previous, current) =>
            previous.touched !== current.touched ||
            (previous.areas.length === current.areas.length &&
              previous.areas.every((val, i) => val === current[i]))
        )
      )
      .subscribe((value) => {
        if (value.touched) {
          this.technicalDataForm.controls.areas.setValue([]);
        }
      });
    this.multiAreaWithMeterActive$ = this.technicalDataForm$.pipe(
      switchMap((form) =>
        form.valueChanges.pipe(
          takeUntilDestroyed(this.destroyRef),
          startWith(form.value),
          combineLatestWith(this.connectionChangeConfig$),
          map(([formValue, config]) => {
            const isDisconnection =
              config.connectionChangeRequestType === ConnectionChangeRequestTypeEnum.DISCONNECTION;
            const meterNumberVisible =
              config.deviceNumberField !== FieldConfigEnum.HIDDEN && !formValue.hasNoMeter;
            const multipleAreasActive = formValue.areas?.length > 1;
            return isDisconnection && meterNumberVisible && multipleAreasActive;
          })
        )
      )
    );
  }

  private initObservables() {
    this.powerUnit$ = this.connectionChangeConfig$.pipe(
      map((config) => config.defaultPowerUnit || 'kW'),
      shareReplay(1)
    );
    this.isCivilEngineering$ = this.technicalDataForm.controls.civilEngineering.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(this.technicalDataForm.controls.civilEngineering.value),
      map((data) => {
        const requiredEnums = [CivilEngineeringEnum.PUBLIC_ONLY, CivilEngineeringEnum.YES];
        return !!data && requiredEnums.includes(data);
      })
    );
    this.hasNoMeter$ = this.technicalDataForm.controls.hasNoMeter.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(this.technicalDataForm.controls.hasNoMeter.value),
      map((value) => !!value)
    );
  }

  private initValidationListener() {
    this.operations$.subscribe(() => {
      Object.values(this.technicalDataForm.controls).forEach((control) => {
        control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      });
      this.technicalDataForm.updateValueAndValidity();
    });
    this.technicalDataForm.controls.installedPower.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.technicalDataForm.controls.requiredPower.updateValueAndValidity();
      });
    this.technicalDataForm.controls.civilEngineering.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.technicalDataForm.controls.suspectedOrdnanceAreaUploads.updateValueAndValidity();
      });
    this.technicalDataForm.controls.hasNoMeter.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), startWith(this.technicalDataForm.controls.hasNoMeter.value))
      .subscribe((hasNoMeter) => {
        const meterNumber = this.technicalDataForm.controls.meterNumber;
        if (hasNoMeter) {
          meterNumber.setValue(null);
        }
        meterNumber.updateValueAndValidity();
      });
  }

  getAvailableAreas(initialArea: AllAreaEnum, allAreas: AllAreaEnum[]) {
    return allAreas.filter((area) => area !== initialArea);
  }

  readonly ConnectionChangeRequestTypeEnum = ConnectionChangeRequestTypeEnum;
  readonly CivilEngineeringOptions = Object.values(CivilEngineeringEnum);
  readonly ConnectionChangeRequestTypeDisplayValues = ConnectionChangeRequestTypeDisplayValues;
  readonly CivilEngineeringDisplayNames = CivilEngineeringDisplayNames;
  readonly AreaDisplayName = AreaDisplayName;
  protected readonly FieldConfigEnum = FieldConfigEnum;
  protected readonly AllAreaEnum = AllAreaEnum;
}
